import React, { useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import SocChartCard from "./SocChartCard";
import { AXIS, GRID, SEVERITY, VIVID, formatDay } from "./socChartTheme";

// Trend charts for Security Operations (/soc), built from GET api/v2/soc/trends. Each series keeps
// the color of its identity, whatever other series are shown.

const NEW_FINDINGS_DEFENDER = [
  { field: "incidents", label: "Incidents", color: VIVID.rose },
  { field: "alerts", label: "Alerts", color: VIVID.orange },
  // New per finding key: one per CVE per device, plus new recommendations, so this is not the unique
  // CVE count the Vulnerabilities section shows. The tooltip and the table read the same label.
  { field: "vulnerabilities", label: "Vulnerabilities & recommendations (new per device)", color: VIVID.violet },
  { field: "otherDefender", label: "Other Defender findings", color: VIVID.indigo },
];
const NEW_FINDINGS_EXTERNAL = { field: "external", label: "External findings", color: VIVID.sky };
// From the org's CVE upgrade (trends.vulnerabilitySource "Exposures") the band's CVE part is new vulnerability
// exposures per Defender snapshot, not new per-device risks; recommendations are counted as before.
const EXPOSURES_LABEL = "New vulnerability exposures & recommendations";
const defenderSeries = (vulnerabilitySource) => (vulnerabilitySource === "Exposures"
  ? NEW_FINDINGS_DEFENDER.map((s) => (s.field === "vulnerabilities" ? { ...s, label: EXPOSURES_LABEL } : s))
  : NEW_FINDINGS_DEFENDER);

// The summary's external event bands, in display order. A band is charted only when its summary
// metric key is present on the external tile.
export const EXTERNAL_BANDS = [
  { metricKey: "esmEvents", field: "esm", label: "Attack surface", color: VIVID.blue },
  { metricKey: "darkWebEvents", field: "darkWeb", label: "Dark web", color: VIVID.violet },
  { metricKey: "secretsEvents", field: "secrets", label: "Exposed secrets", color: VIVID.amber },
  { metricKey: "brandEvents", field: "brand", label: "Brand threats", color: VIVID.pink },
  { metricKey: "ransomwareEvents", field: "ransomware", label: "Ransomware", color: VIVID.rose },
];

const count = (point, field) => Number(point?.[field]) || 0;

// The stacked charts' tooltip: the day, each series with its colour and count (largest first, zeros
// left out) and the day's total. Exported for tests: Recharts only renders it on a real pointer position.
// `keepOrder` lists the series in their given order instead of largest first (severities read top-down).
// The last day of a range is today (UTC), still being counted: its axis label is TODAY_LABEL and its
// tooltip and table say "so far", so its smaller count doesn't read as a real drop.
export const TODAY_LABEL = "Today";
const TODAY_DETAIL = "Today (so far)";
const dayLabel = (date, todayKey) => (String(date || "").slice(0, 10) === todayKey ? TODAY_LABEL : formatDay(date));

export const ActivityTooltip = ({ active, payload, label, series, emptyText = "No new findings", keepOrder = false }) => {
  if (!active || !payload || payload.length === 0) return null;
  const byField = Object.fromEntries(payload.map((p) => [p.dataKey, Number(p.value) || 0]));
  const rows = series
    .map((s) => ({ ...s, value: byField[s.field] || 0 }))
    .filter((s) => s.value > 0)
    .sort((a, b) => (keepOrder ? 0 : b.value - a.value));
  const total = rows.reduce((sum, s) => sum + s.value, 0);
  return (
    <div className="min-w-[11rem] rounded-lg border border-border bg-theme-card-solid px-3 py-2 text-xs shadow-lg" data-testid="soc-activity-tooltip">
      <p className="mb-1 font-semibold text-content-primary">{label === TODAY_LABEL ? TODAY_DETAIL : label}</p>
      {rows.length === 0 ? (
        <p className="text-content-muted">{emptyText}</p>
      ) : (
        <ul className="space-y-0.5">
          {rows.map((s) => (
            <li key={s.field} className="flex items-center gap-2">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
              <span className="text-content-secondary">{s.label}</span>
              <span className="ml-auto pl-3 font-semibold tabular-nums text-content-primary">{s.value}</span>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 1 && (
        <p className="mt-1 flex border-t border-border pt-1 font-semibold text-content-primary">
          Total<span className="ml-auto tabular-nums">{total}</span>
        </p>
      )}
    </div>
  );
};

// Daily stacked series over dense, oldest-first points: bars, or (`area`) gradient-filled areas.
// Either highlights one series when its band or its legend entry is pointed at (or the legend entry
// is focused): that series comes forward, the others fade. `tooltipEmpty` is the tooltip's text for a
// day with nothing. `now` (tests) decides which day is today.
const StackedDaily = ({ testId, title, subtitle, emptyText, points, series, area = false, height, tooltipEmpty, now }) => {
  const [activeField, setActiveField] = useState(null);
  const todayKey = (now || new Date()).toISOString().slice(0, 10);
  const rows = (points || []).map((p) => {
    const row = { label: dayLabel(p.date, todayKey) };
    series.forEach(({ field }) => {
      row[field] = count(p, field);
    });
    return row;
  });
  const total = rows.reduce((sum, row) => sum + series.reduce((s, { field }) => s + row[field], 0), 0);

  return (
    <SocChartCard
      testId={testId}
      title={title}
      subtitle={subtitle}
      empty={series.length === 0 || total === 0}
      emptyText={emptyText}
      legend={series.map(({ label, color }) => ({ label, color }))}
      table={{
        columns: ["Day", ...series.map(({ label }) => label)],
        rows: rows.map((row) => [row.label === TODAY_LABEL ? TODAY_DETAIL : row.label, ...series.map(({ field }) => row[field])]),
      }}
      height={height}
      activeLegend={series.find((s) => s.field === activeField)?.label ?? null}
      onLegendHover={(label) => setActiveField(series.find((s) => s.label === label)?.field ?? null)}
    >
      <ResponsiveContainer width="100%" height="100%">
        {area ? (
          <AreaChart data={rows} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
            <defs>
              {series.map(({ field, color }) => (
                <linearGradient key={field} id={`${testId}-${field}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.55 }} />
                  <stop offset="100%" style={{ stopColor: color, stopOpacity: 0.04 }} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={24} padding={{ left: 4, right: 12 }} {...AXIS} />
            <YAxis allowDecimals={false} width={32} {...AXIS} />
            <Tooltip
              content={<ActivityTooltip series={series} emptyText={tooltipEmpty} />}
              cursor={{ stroke: "var(--accent-cyan)", strokeWidth: 1, strokeDasharray: "4 3" }}
            />
            {series.map(({ field, label, color }) => {
              const faded = !!activeField && activeField !== field;
              const on = activeField === field;
              return (
                <Area
                  key={field}
                  type="monotone"
                  dataKey={field}
                  name={label}
                  stackId="a"
                  stroke={color}
                  strokeWidth={on ? 3 : 2}
                  strokeOpacity={faded ? 0.25 : 1}
                  fill={`url(#${testId}-${field})`}
                  fillOpacity={faded ? 0.15 : 1}
                  dot={false}
                  activeDot={{ r: 5, stroke: "var(--bg-card-solid)", strokeWidth: 2 }}
                  isAnimationActive={false}
                  onMouseEnter={() => setActiveField(field)}
                  onMouseLeave={() => setActiveField(null)}
                />
              );
            })}
          </AreaChart>
        ) : (
        <BarChart data={rows} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={16} {...AXIS} />
          <YAxis allowDecimals={false} width={32} {...AXIS} />
          <Tooltip
            content={<ActivityTooltip series={series} emptyText={tooltipEmpty} />}
            cursor={{ fill: "var(--accent-cyan-muted)" }}
          />
          {series.map(({ field, label, color }) => (
            <Bar
              key={field}
              dataKey={field}
              name={label}
              stackId="a"
              fill={color}
              fillOpacity={activeField && activeField !== field ? 0.2 : 1}
              stroke="var(--bg-card-solid)"
              strokeWidth={1}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
              onMouseEnter={() => setActiveField(field)}
              onMouseLeave={() => setActiveField(null)}
            />
          ))}
        </BarChart>
        )}
      </ResponsiveContainer>
    </SocChartCard>
  );
};

// `fields`: chart only these series (a SOC area view), all applicable ones when absent.
// Gradient stacked areas: the Overview's headline threat-activity chart.
// `rangeDays`, when given, is named in the subtitle so the chart isn't read as a tile's fixed window.
const inRange = (text, rangeDays) => (rangeDays ? `${text} · last ${rangeDays} days` : text);

export const SocNewFindingsChart = ({ points, showDefender, showExternal, fields, height, emptyText = "No new findings in this period.", rangeDays = null, now, vulnerabilitySource }) => {
  const series = [...(showDefender ? defenderSeries(vulnerabilitySource) : []), ...(showExternal ? [NEW_FINDINGS_EXTERNAL] : [])]
    .filter((s) => !fields || fields.includes(s.field));

  return (
    <StackedDaily
      area
      height={height}
      testId="soc-chart-new-findings"
      title="Threat activity"
      subtitle={inRange("New findings per day by source, counted on the day Axaris first recorded each (a newly connected source shows its backlog on its first day)", rangeDays)}
      emptyText={emptyText}
      points={points}
      series={series}
      now={now}
    />
  );
};

export const SocExternalActivityChart = ({ points, bands, emptyText = "No external threat events in this period.", rangeDays = null, now }) => (
  <StackedDaily
    tooltipEmpty="No events"
    testId="soc-chart-external"
    title="External threat activity"
    subtitle={inRange("Events per day", rangeDays)}
    emptyText={emptyText}
    points={points}
    series={bands || []}
    now={now}
  />
);

// The opened / resolved charts' tooltip: the day, both counts and the day's net change. Exported for
// tests: Recharts only renders it on a real pointer position.
export const OpenedResolvedTooltip = ({ active, payload, label, openedLabel, resolvedLabel }) => {
  if (!active || !payload || payload.length === 0) return null;
  const value = (key) => Number(payload.find((p) => p.dataKey === key)?.value) || 0;
  const opened = value("opened");
  const resolved = value("resolved");
  const net = opened - resolved;
  const verb = openedLabel.toLowerCase();
  const done = resolvedLabel.toLowerCase();
  return (
    <div className="min-w-[11rem] rounded-lg border border-border bg-theme-card-solid px-3 py-2 text-xs shadow-lg" data-testid="soc-opened-resolved-tooltip">
      <p className="mb-1 font-semibold text-content-primary">{label === TODAY_LABEL ? TODAY_DETAIL : label}</p>
      <ul className="space-y-0.5">
        {[[openedLabel, opened, VIVID.orange], [resolvedLabel, resolved, VIVID.green]].map(([name, n, color]) => (
          <li key={name} className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />
            <span className="text-content-secondary">{name}</span>
            <span className="ml-auto pl-3 font-semibold tabular-nums text-content-primary">{n}</span>
          </li>
        ))}
      </ul>
      <p className={`mt-1 border-t border-border pt-1 font-semibold ${net > 0 ? "text-status-error" : net < 0 ? "text-status-success" : "text-content-muted"}`}>
        {net > 0 ? `+${net} more ${verb} than ${done}`
          : net < 0 ? `${-net} more ${done} than ${verb}`
            : opened === 0 ? "Nothing that day" : `As many ${done} as ${verb}`}
      </p>
    </div>
  );
};

// Grouped (not stacked) bars: what was opened each day next to what was resolved, over dense,
// oldest-first points. Pointing at a day shows both counts and the net; pointing at a series' bars,
// or at (or focusing) its legend entry, keeps that series and fades the other.
export const SocOpenedResolvedChart = ({ testId, title, subtitle, points, openedLabel, resolvedLabel, emptyText, now }) => {
  const [activeField, setActiveField] = useState(null);
  const faded = (field) => !!activeField && activeField !== field;
  const fieldOf = (label) => (label === openedLabel ? "opened" : label === resolvedLabel ? "resolved" : null);
  const todayKey = (now || new Date()).toISOString().slice(0, 10);
  const rows = (points || []).map((p) => ({
    label: dayLabel(p.date, todayKey),
    opened: count(p, "opened"),
    resolved: count(p, "resolved"),
  }));
  const total = rows.reduce((sum, row) => sum + row.opened + row.resolved, 0);

  return (
    <SocChartCard
      testId={testId}
      title={title}
      subtitle={subtitle}
      empty={total === 0}
      emptyText={emptyText}
      legend={[
        { label: openedLabel, color: VIVID.orange },
        { label: resolvedLabel, color: VIVID.green },
      ]}
      activeLegend={activeField === "opened" ? openedLabel : activeField === "resolved" ? resolvedLabel : null}
      onLegendHover={(label) => setActiveField(label ? fieldOf(label) : null)}
      table={{
        columns: ["Day", openedLabel, resolvedLabel],
        rows: rows.map((row) => [row.label, row.opened, row.resolved]),
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} barGap={2} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={16} {...AXIS} />
          <YAxis allowDecimals={false} width={32} {...AXIS} />
          <Tooltip
            content={<OpenedResolvedTooltip openedLabel={openedLabel} resolvedLabel={resolvedLabel} />}
            cursor={{ fill: "var(--accent-cyan-muted)" }}
          />
          <Bar dataKey="opened" name={openedLabel} fill={VIVID.orange} radius={[4, 4, 0, 0]}
            fillOpacity={faded("opened") ? 0.2 : 1} isAnimationActive={false}
            onMouseEnter={() => setActiveField("opened")} onMouseLeave={() => setActiveField(null)} />
          <Bar dataKey="resolved" name={resolvedLabel} fill={VIVID.green} radius={[4, 4, 0, 0]}
            fillOpacity={faded("resolved") ? 0.2 : 1} isAnimationActive={false}
            onMouseEnter={() => setActiveField("resolved")} onMouseLeave={() => setActiveField(null)} />
        </BarChart>
      </ResponsiveContainer>
    </SocChartCard>
  );
};

// Stack order bottom-to-top; the legend and table read top-down (Critical first).
// Medium and above only: the snapshot's Low band holds nearly every identity (those with little or no
// exposure), so charting it as "at risk" contradicts the most-exposed list and the identity tile.
const IDENTITY_STACK = [
  { field: "medium", label: "Medium", color: SEVERITY.medium },
  { field: "high", label: "High", color: SEVERITY.high },
  { field: "critical", label: "Critical", color: SEVERITY.critical },
];
const IDENTITY_TOP_DOWN = [...IDENTITY_STACK].reverse();

// Identities at risk by severity. The points are sparse (one per stored daily snapshot), so only the
// days present are plotted; a missing day is never drawn as 0. Counts only: Exposure (money) is not
// charted. Pointing at a snapshot shows its counts Critical first; pointing at a severity's band, or
// at (or focusing) its legend entry, brings it forward and fades the others.
export const SocIdentityExposureChart = ({ points, emptyText = "No identity exposure history yet. Axaris records one snapshot a day." }) => {
  const [activeField, setActiveField] = useState(null);
  const rows = (points || []).map((p) => {
    const row = { label: formatDay(p.date) };
    IDENTITY_STACK.forEach(({ field }) => {
      row[field] = count(p, field);
    });
    return row;
  });

  return (
    <SocChartCard
      testId="soc-chart-identity"
      title="Identity exposure"
      subtitle="Identities at medium, high or critical exposure, from the daily snapshot. Low-exposure identities aren't charted."
      empty={rows.length === 0}
      emptyText={emptyText}
      legend={IDENTITY_TOP_DOWN.map(({ label, color }) => ({ label, color }))}
      activeLegend={IDENTITY_STACK.find((s) => s.field === activeField)?.label ?? null}
      onLegendHover={(label) => setActiveField(IDENTITY_STACK.find((s) => s.label === label)?.field ?? null)}
      table={{
        columns: ["Day", ...IDENTITY_TOP_DOWN.map(({ label }) => label)],
        rows: rows.map((row) => [row.label, ...IDENTITY_TOP_DOWN.map(({ field }) => row[field])]),
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={16} {...AXIS} />
          <YAxis allowDecimals={false} width={32} {...AXIS} />
          <Tooltip
            content={<ActivityTooltip series={IDENTITY_TOP_DOWN} emptyText="No identities at risk" keepOrder />}
            cursor={{ stroke: "var(--accent-cyan)", strokeWidth: 1, strokeDasharray: "4 3" }}
          />
          {IDENTITY_STACK.map(({ field, label, color }) => {
            const on = activeField === field;
            const faded = !!activeField && !on;
            return (
              <Area
                key={field}
                type="monotone"
                dataKey={field}
                name={label}
                stackId="a"
                stroke={color}
                strokeWidth={on ? 3 : 2}
                strokeOpacity={faded ? 0.25 : 1}
                fill={color}
                fillOpacity={on ? 0.5 : faded ? 0.06 : 0.25}
                dot={false}
                activeDot={{ r: 5, stroke: "var(--bg-card-solid)", strokeWidth: 2 }}
                isAnimationActive={false}
                onMouseEnter={() => setActiveField(field)}
                onMouseLeave={() => setActiveField(null)}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </SocChartCard>
  );
};
